import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import emailjs from "@emailjs/browser";

const PilotIntakeContext = createContext(null);

const EMAILJS_PUBLIC_KEY = "2M14dLmoA057sBkV8";
const EMAILJS_SERVICE_ID = "service_byxrhtf";
const EMAILJS_TEMPLATE_ID = "template_vnwlbdl";
const GOOGLE_ADS_CONVERSION_ID = "AW-18251804270/bf15CNPv3MEcEO7ckf9D";

const INITIAL_FORM = {
  name: "",
  email: "",
  institution: "",
  students: "",
  role: "",
  timeline: "",
  notes: "",
};

const QUESTIONS = [
  {
    key: "name",
    eyebrow: "First, let's meet",
    title: "What is your name?",
    placeholder: "Dr. Maya Patel",
    type: "text",
    autoComplete: "name",
    required: true,
  },
  {
    key: "email",
    eyebrow: "Where should we reply?",
    title: "What is your email address?",
    placeholder: "maya@university.edu",
    type: "email",
    autoComplete: "email",
    required: true,
  },
  {
    key: "institution",
    eyebrow: "Your campus",
    title: "What institution are you with?",
    placeholder: "University of Nebraska-Lincoln",
    type: "text",
    autoComplete: "organization",
    required: true,
  },
  {
    key: "students",
    eyebrow: "Pilot size",
    title: "How many students do you need this for?",
    placeholder: "120",
    type: "number",
    inputMode: "numeric",
    min: "1",
    required: true,
  },
  {
    key: "role",
    eyebrow: "Your role",
    title: "Which best describes you?",
    type: "choice",
    required: true,
    options: ["Instructor", "Department lead", "Instructional designer", "Administrator"],
  },
  {
    key: "timeline",
    eyebrow: "Launch timing",
    title: "When would you like to run the pilot?",
    type: "choice",
    required: true,
    options: ["This semester", "Next semester", "Fall 2026", "Still exploring"],
  },
  {
    key: "notes",
    eyebrow: "One last thing",
    title: "Anything we should know before reaching out?",
    placeholder: "Course format, LMS, department needs, or rollout details",
    type: "textarea",
    required: false,
  },
];

export function PilotIntakeProvider({ children }) {
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  const question = QUESTIONS[step];
  const isLastQuestion = step === QUESTIONS.length - 1;
  const answeredCount = QUESTIONS.filter((q) => String(formData[q.key] || "").trim()).length;
  const progress = status === "success" ? 100 : Math.round(((step + 1) / QUESTIONS.length) * 100);

  const openPilotIntake = () => {
    setIsOpen(true);
    setStatus("idle");
    setError("");
  };

  const closePilotIntake = () => {
    setIsOpen(false);
    setStep(0);
    setStatus("idle");
    setError("");
    setFormData(INITIAL_FORM);
  };

  useEffect(() => {
    if (!isOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKeyDown = (event) => {
      if (event.key === "Escape") closePilotIntake();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || status !== "idle") return;
    const timer = window.setTimeout(() => {
      inputRef.current?.focus();
    }, 160);
    return () => window.clearTimeout(timer);
  }, [isOpen, step, status]);

  const updateField = (key, value) => {
    setFormData((current) => ({ ...current, [key]: value }));
    setError("");
  };

  const isCurrentValid = () => {
    if (!question.required) return true;
    const value = String(formData[question.key] || "").trim();
    if (!value) return false;
    if (question.type === "email") return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
    if (question.type === "number") return Number(value) > 0;
    return true;
  };

  const submitPilotRequest = async () => {
    setStatus("sending");
    setError("");

    emailjs.init(EMAILJS_PUBLIC_KEY);

    const message = [
      "New CoursEngage pilot request",
      "",
      `Name: ${formData.name}`,
      `Email: ${formData.email}`,
      `Institution: ${formData.institution}`,
      `Number of students: ${formData.students}`,
      `Role: ${formData.role}`,
      `Timeline: ${formData.timeline}`,
      `Notes: ${formData.notes || "None provided"}`,
    ].join("\n");

    try {
      await emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_TEMPLATE_ID, {
        from_name: formData.name,
        subject: `Pilot request from ${formData.institution || formData.name}`,
        message,
        email: formData.email,
        reply_to: formData.email,
      });
      if (typeof window.gtag === "function") {
        window.gtag("event", "conversion", {
          send_to: GOOGLE_ADS_CONVERSION_ID,
          value: 1.0,
          currency: "USD",
        });
      }
      setStatus("success");
    } catch (err) {
      setStatus("idle");
      setError("Something went wrong while sending. Please try again.");
    }
  };

  const handleNext = async () => {
    if (!isCurrentValid()) {
      setError(question.type === "email" ? "Please enter a valid email address." : "Please answer this before continuing.");
      return;
    }

    if (isLastQuestion) {
      await submitPilotRequest();
      return;
    }

    setStep((current) => current + 1);
  };

  const handleBack = () => {
    setError("");
    setStep((current) => Math.max(current - 1, 0));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    handleNext();
  };

  const value = useMemo(() => ({ openPilotIntake }), []);

  return (
    <PilotIntakeContext.Provider value={value}>
      {children}
      {isOpen && (
        <div className="ce-pilot-overlay" role="dialog" aria-modal="true" aria-labelledby="pilot-question-title">
          <button className="ce-pilot-scrim" type="button" aria-label="Close pilot form" onClick={closePilotIntake} />

          <div className="ce-pilot-shell">
            <div className="ce-pilot-progress" aria-hidden="true">
              <span style={{ width: `${progress}%` }} />
            </div>

            <button className="ce-pilot-close" type="button" onClick={closePilotIntake} aria-label="Close pilot form">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M6 6l12 12M18 6L6 18" />
              </svg>
            </button>

            {status === "success" ? (
              <div className="ce-pilot-success">
                <span className="ce-pilot-check" aria-hidden="true">
                  <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                </span>
                <p className="ce-pilot-eyebrow">Request received</p>
                <h2>Thank you.</h2>
                <p>We will get back to you shortly.</p>
                <button type="button" className="ce-btn ce-btn-primary" onClick={closePilotIntake}>
                  Done
                </button>
              </div>
            ) : (
              <form className="ce-pilot-form" onSubmit={handleSubmit}>
                <div className="ce-pilot-meta">
                  <span>{question.eyebrow}</span>
                  <span>{Math.min(step + 1, QUESTIONS.length)} of {QUESTIONS.length}</span>
                </div>

                <div className="ce-pilot-question" key={question.key}>
                  <h2 id="pilot-question-title">{question.title}</h2>

                  {question.type === "choice" ? (
                    <div className="ce-pilot-choices">
                      {question.options.map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={formData[question.key] === option ? "is-selected" : ""}
                          onClick={() => updateField(question.key, option)}
                        >
                          {option}
                        </button>
                      ))}
                    </div>
                  ) : question.type === "textarea" ? (
                    <textarea
                      ref={inputRef}
                      value={formData[question.key]}
                      onChange={(event) => updateField(question.key, event.target.value)}
                      placeholder={question.placeholder}
                      rows="4"
                    />
                  ) : (
                    <input
                      ref={inputRef}
                      type={question.type}
                      value={formData[question.key]}
                      onChange={(event) => updateField(question.key, event.target.value)}
                      placeholder={question.placeholder}
                      autoComplete={question.autoComplete}
                      inputMode={question.inputMode}
                      min={question.min}
                    />
                  )}
                </div>

                {error && <p className="ce-pilot-error">{error}</p>}

                <div className="ce-pilot-actions">
                  <button type="button" className="ce-btn ce-btn-secondary" onClick={handleBack} disabled={step === 0 || status === "sending"}>
                    Back
                  </button>
                  <button type="submit" className="ce-btn ce-btn-primary" disabled={status === "sending"}>
                    {status === "sending" ? "Sending..." : isLastQuestion ? "Send request" : "Continue"}
                    {status !== "sending" && (
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M5 12h14M13 5l7 7-7 7" />
                      </svg>
                    )}
                  </button>
                </div>

                <div className="ce-pilot-footnote">
                  {answeredCount} answers saved locally until you submit.
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </PilotIntakeContext.Provider>
  );
}

export function usePilotIntake() {
  const context = useContext(PilotIntakeContext);
  if (!context) {
    throw new Error("usePilotIntake must be used inside PilotIntakeProvider");
  }
  return context;
}

export function PilotButton({ children, className = "", ...props }) {
  const { openPilotIntake } = usePilotIntake();

  return (
    <button type="button" className={className} onClick={openPilotIntake} {...props}>
      {children}
    </button>
  );
}
